import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Send, Mail, Clock, AlertTriangle, CheckCircle2, ChevronRight,
  Reply, Forward, Paperclip, Lock, Truck, Calendar
} from "lucide-react";

const typeLabels = {
  'consultant-chase': 'Consultant chase', 'surveyor-update': 'Surveyor update',
  'formal-request': 'Formal request', 'certificate-chase': 'Certificate chase',
};
const statusConfig = {
  'awaiting-reply': { label: 'Awaiting reply', badge: 'bg-amber-100 text-amber-700' },
  'sent': { label: 'Sent', badge: 'bg-blue-100 text-blue-700' },
  'replied': { label: 'Replied', badge: 'bg-emerald-100 text-emerald-700' },
};

export default function EmailActionRail() {
  const { emailThreads, logAction } = useDemoState();
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const awaiting = emailThreads.filter(t => t.status === 'awaiting-reply');
  const filtered = activeTab === 'awaiting' ? awaiting : emailThreads;

  const handleSend = (thread, action) => {
    logAction(`Email ${action}: ${thread.subject}`, 'email');
    toast.success(`${action} sent`, { description: `To ${thread.recipient}` });
  };

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="email-rail-page">
        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total threads', value: emailThreads.length, color: 'text-slate-900' },
            { label: 'Awaiting reply', value: awaiting.length, color: 'text-amber-600' },
            { label: 'Sent', value: emailThreads.filter(t => t.status === 'sent').length, color: 'text-blue-600' },
          ].map(kpi => (
            <div key={kpi.label} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{kpi.label}</p>
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
          <Mail className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700">Email is the formal channel for consultant requests, certificate chasers, surveyor updates, and document-heavy communication. Use WhatsApp for fast chase.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" className="text-xs">All threads</TabsTrigger>
            <TabsTrigger value="awaiting" className="text-xs">Awaiting reply ({awaiting.length})</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 space-y-2">
            {filtered.map(thread => {
              const sc = statusConfig[thread.status] || statusConfig.sent;
              return (
                <button key={thread.id} onClick={() => setSelected(thread)}
                  data-testid={`email-thread-${thread.id}`}
                  className="w-full text-left flex items-center gap-4 px-4 py-3 bg-white rounded-lg border border-slate-200 hover:shadow-sm transition-all group">
                  <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                    {thread.recipient.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">{thread.subject}</p>
                    </div>
                    <p className="text-xs text-slate-500">{thread.recipient} ({thread.recipientRole}) · {thread.project}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Last: {thread.sentDate} · {thread.messages.length} message{thread.messages.length > 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-[10px] ${sc.badge}`}>{sc.label}</Badge>
                    <Badge className="text-[10px] bg-slate-100 text-slate-500">{typeLabels[thread.type] || thread.type}</Badge>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
                </button>
              );
            })}
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
        <SheetContent side="right" className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="email-detail-sheet">
          {selected && (
            <div className="space-y-4">
              <SheetHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-[10px] ${(statusConfig[selected.status] || statusConfig.sent).badge}`}>{(statusConfig[selected.status] || statusConfig.sent).label}</Badge>
                  <Badge className="text-[10px] bg-slate-100 text-slate-500">{typeLabels[selected.type]}</Badge>
                </div>
                <SheetTitle className="text-base">{selected.subject}</SheetTitle>
                <SheetDescription>To: {selected.recipient} ({selected.recipientEmail})</SheetDescription>
              </SheetHeader>

              {/* Thread */}
              <div className="space-y-3">
                {selected.messages.map((msg, i) => (
                  <div key={i} className={`rounded-lg border p-4 ${msg.direction === 'out' ? 'bg-white border-slate-200' : 'bg-blue-50 border-blue-100'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {msg.direction === 'out' ? <Send className="w-3 h-3 text-slate-400" /> : <Reply className="w-3 h-3 text-blue-400" />}
                        <span className="text-[10px] font-medium text-slate-500">{msg.direction === 'out' ? 'Sent' : 'Received'}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">{msg.date}</span>
                    </div>
                    <p className="text-xs font-medium text-slate-700 mb-2">{msg.subject}</p>
                    <pre className="text-xs text-slate-600 whitespace-pre-wrap font-body leading-relaxed">{msg.body}</pre>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => handleSend(selected, 'Follow-up')} className="bg-slate-900 hover:bg-slate-800 gap-1.5 text-xs"><Send className="w-3.5 h-3.5" />Send follow-up</Button>
                <Button size="sm" variant="outline" onClick={() => handleSend(selected, 'Reminder')} className="gap-1.5 text-xs"><Clock className="w-3.5 h-3.5" />Schedule reminder</Button>
                <Button size="sm" variant="outline" className="gap-1.5 text-xs"><Calendar className="w-3.5 h-3.5" />Add to calendar</Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Link to="/builder/job-blocker" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-[11px] font-medium text-slate-600 hover:text-blue-600 transition-all"><AlertTriangle className="w-3 h-3" />Job Blocker</Link>
                <Link to="/builder/whatsapp-action-rail" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-[11px] font-medium text-slate-600 hover:text-blue-600 transition-all"><Send className="w-3 h-3" />WhatsApp Rail</Link>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
